import { useState } from "react";
import { ShieldAlert, KeyRound, Loader2, LogOut, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const RULES = [
  { key: "len", label: "At least 8 characters", test: (v) => v.length >= 8 },
  { key: "upper", label: "An uppercase letter", test: (v) => /[A-Z]/.test(v) },
  { key: "lower", label: "A lowercase letter", test: (v) => /[a-z]/.test(v) },
  { key: "digit", label: "A digit", test: (v) => /\d/.test(v) },
  { key: "special", label: "A special character", test: (v) => /[^A-Za-z0-9]/.test(v) },
];

export default function ForcedPasswordChange() {
  const { user, refresh, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const expired = !!user?.password_expired;
  const rulesOk = RULES.every((r) => r.test(next));
  const matchOk = next.length > 0 && next === confirm;
  const canSubmit = current.length > 0 && rulesOk && matchOk && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!matchOk) { toast.error("New password and confirmation do not match."); return; }
    setBusy(true);
    try {
      await api.post("/security/password/change", { current_password: current, new_password: next });
      toast.success("Password updated. Welcome aboard!");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4" data-testid="forced-pw-screen">
      <Card className="w-full max-w-md shadow-xl border-border">
        <CardHeader className="space-y-3">
          <div className="flex items-center gap-2.5">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 text-amber-700">
              <ShieldAlert className="h-5 w-5" />
            </span>
            <div>
              <CardTitle className="text-lg">Set a new password</CardTitle>
              <p className="text-xs text-muted-foreground">Required before you continue</p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground" data-testid="forced-pw-reason">
            {expired
              ? "Your temporary password has expired. Please choose a new password to activate your account."
              : "You're signing in with a temporary password. For your security, please choose a new password now."}
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs" htmlFor="fpw-current">Temporary password</Label>
              <Input id="fpw-current" type="password" autoComplete="current-password" value={current}
                onChange={(e) => setCurrent(e.target.value)} placeholder="Enter the password you just used"
                data-testid="forced-pw-current" />
            </div>
            <div>
              <Label className="text-xs" htmlFor="fpw-new">New password</Label>
              <Input id="fpw-new" type="password" autoComplete="new-password" value={next}
                onChange={(e) => setNext(e.target.value)} placeholder="Create a strong password"
                data-testid="forced-pw-new" />
            </div>
            <div>
              <Label className="text-xs" htmlFor="fpw-confirm">Confirm new password</Label>
              <Input id="fpw-confirm" type="password" autoComplete="new-password" value={confirm}
                onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter the new password"
                data-testid="forced-pw-confirm" />
              {confirm.length > 0 && !matchOk && (
                <p className="mt-1 text-xs text-destructive" data-testid="forced-pw-mismatch">Passwords do not match.</p>
              )}
            </div>
            <ul className="grid grid-cols-2 gap-1.5 text-xs" data-testid="forced-pw-rules">
              {RULES.map((r) => {
                const ok = r.test(next);
                return (
                  <li key={r.key} className={`flex items-center gap-1.5 ${ok ? "text-emerald-600" : "text-muted-foreground"}`}>
                    <CheckCircle2 className={`h-3.5 w-3.5 ${ok ? "opacity-100" : "opacity-40"}`} />
                    {r.label}
                  </li>
                );
              })}
            </ul>
            <Button type="submit" className="w-full" disabled={!canSubmit} data-testid="forced-pw-submit">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <KeyRound className="h-4 w-4 mr-2" />}
              Update password &amp; continue
            </Button>
            <button type="button" onClick={logout}
              className="w-full text-xs text-muted-foreground hover:text-foreground flex items-center justify-center gap-1.5"
              data-testid="forced-pw-logout">
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
